import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { RoundData } from './QuizGame';
import { SpecialtyCard } from './SpecialtyCard';
import { Specialty, SPECIALTY_COLORS } from '../types';
import { CheckCircle2, XCircle, Lightbulb, ArrowRight, Flame, Trophy, Frown } from 'lucide-react';
interface QuizBoardProps {
  roundData: RoundData;
  currentRoundNum: number;
  totalRounds: number;
  onRoundComplete: (score: number) => void;
}
const SPECIALTIES: Specialty[] = ['Nephrologist', 'Cardiologist', 'Endocrinologist', 'PCP'];
export function QuizBoard({
  roundData,
  currentRoundNum,
  totalRounds,
  onRoundComplete
}: QuizBoardProps) {
  const [completedSpecialties, setCompletedSpecialties] = useState<Specialty[]>([]);
  const [selectedSpecialty, setSelectedSpecialty] = useState<Specialty | null>(null);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [showInsight, setShowInsight] = useState(false);
  const [roundScore, setRoundScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [lastResult, setLastResult] = useState<'correct' | 'wrong' | null>(null);
  const [showFlash, setShowFlash] = useState(false);
  const [floatingScore, setFloatingScore] = useState<{
    value: number;
    key: number;
  } | null>(null);
  const [shakeScreen, setShakeScreen] = useState(false);
  const [particles, setParticles] = useState<Array<{
    id: number;
    x: number;
    y: number;
    color: string;
  }>>([]);
  // Reset state when round changes
  useEffect(() => {
    setCompletedSpecialties([]);
    setSelectedSpecialty(null);
    setSelectedOption(null);
    setShowInsight(false);
    setRoundScore(0);
  }, [roundData.id]);
  const spawnParticles = () => {
    const colors = ['#22c55e', '#4ade80', '#86efac', '#fbbf24', '#f59e0b'];
    const newParticles = Array.from({
      length: 16
    }, (_, i) => ({
      id: Date.now() + i,
      x: Math.random() * 100,
      y: Math.random() * 40 + 30,
      color: colors[Math.floor(Math.random() * colors.length)]
    }));
    setParticles(newParticles);
    setTimeout(() => setParticles([]), 1500);
  };
  const handleSpecialtyClick = (specialty: Specialty) => {
    if (!completedSpecialties.includes(specialty) && !selectedSpecialty) {
      setSelectedSpecialty(specialty);
    }
  };
  const handleOptionSelect = (option: string) => {
    if (selectedOption || !selectedSpecialty) return;
    setSelectedOption(option);
    const isCorrect = option === roundData.answers[selectedSpecialty];
    if (isCorrect) {
      setRoundScore(prev => prev + 1);
      setStreak(prev => prev + 1);
      setLastResult('correct');
      setFloatingScore({
        value: 1,
        key: Date.now()
      });
      spawnParticles();
    } else {
      setStreak(0);
      setLastResult('wrong');
      setFloatingScore({
        value: -1,
        key: Date.now()
      });
      setShakeScreen(true);
      setTimeout(() => setShakeScreen(false), 500);
    }
    setShowFlash(true);
    setTimeout(() => setShowFlash(false), 600);
    setTimeout(() => {
      setShowInsight(true);
    }, 900);
  };
  const handleContinue = () => {
    if (!selectedSpecialty) return;
    const newCompleted = [...completedSpecialties, selectedSpecialty];
    setCompletedSpecialties(newCompleted);
    setSelectedSpecialty(null);
    setSelectedOption(null);
    setShowInsight(false);
    setLastResult(null);
    setFloatingScore(null);
    if (newCompleted.length === SPECIALTIES.length) {
      setTimeout(() => {
        onRoundComplete(roundScore);
      }, 500);
    }
  };
  return <motion.div animate={shakeScreen ? {
    x: [0, -8, 8, -6, 6, -3, 3, 0]
  } : {
    x: 0
  }} transition={{
    duration: 0.5
  }} className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4 md:px-6 flex flex-col relative overflow-hidden" data-id="element-160">
      {/* Celebration particles */}
      <AnimatePresence data-id="element-161">
        {particles.map(p => <motion.div key={p.id} initial={{
        opacity: 1,
        y: `${p.y}vh`,
        x: `${p.x}vw`,
        scale: 1
      }} animate={{
        opacity: 0,
        y: `${p.y - 30}vh`,
        x: `${p.x + (Math.random() - 0.5) * 20}vw`,
        scale: 0,
        rotate: Math.random() * 360
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 1.2,
        ease: 'easeOut'
      }} className="fixed z-[100] pointer-events-none" data-id="element-162">
            <div className="w-3 h-3 rounded-full" style={{
          backgroundColor: p.color
        }} data-id="element-163" />
          </motion.div>)}
      </AnimatePresence>

      {/* Full-screen flash overlay */}
      <AnimatePresence data-id="element-164">
        {showFlash && <motion.div initial={{
        opacity: 0.6
      }} animate={{
        opacity: 0
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.6
      }} className={`fixed inset-0 z-[90] pointer-events-none ${lastResult === 'correct' ? 'bg-green-400' : 'bg-red-400'}`} data-id="element-165" />}
      </AnimatePresence>

      {/* Floating score badge */}
      <AnimatePresence data-id="element-166">
        {floatingScore && <motion.div key={floatingScore.key} initial={{
        opacity: 1,
        y: 0,
        scale: 0.5
      }} animate={{
        opacity: 0,
        y: -120,
        scale: 1.5
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 1.2,
        ease: 'easeOut'
      }} className="fixed top-1/3 left-1/2 -translate-x-1/2 z-[95] pointer-events-none" data-id="element-167">
            <span className={`text-6xl md:text-8xl font-black ${floatingScore.value > 0 ? 'text-green-500' : 'text-red-500'}`} data-id="element-168">
              {floatingScore.value > 0 ? '+1' : '−1'}
            </span>
          </motion.div>}
      </AnimatePresence>

      <div className="max-w-5xl w-full mx-auto flex-grow flex flex-col" data-id="element-169">
        {/* Score Bar */}
        <div className="flex flex-wrap justify-center gap-3 mb-6" data-id="element-170">
          <div className="bg-white rounded-full px-5 py-2 shadow-sm border border-gray-200 flex items-center gap-2" data-id="element-171">
            <Trophy size={18} className="text-brand" data-id="element-172" />
            <span className="text-sm font-bold text-gray-500 uppercase tracking-wider" data-id="element-173">
              Score
            </span>
            <span className="text-xl font-black text-dark" data-id="element-174">{roundScore}/4</span>
          </div>
          <div className={`rounded-full px-5 py-2 shadow-sm border flex items-center gap-2 transition-all duration-300 ${streak >= 2 ? 'bg-amber-50 border-amber-300' : 'bg-white border-gray-200'}`} data-id="element-175">
            <Flame size={18} className={streak >= 2 ? 'text-amber-500' : 'text-gray-400'} data-id="element-176" />
            <span className="text-sm font-bold text-gray-500 uppercase tracking-wider" data-id="element-177">
              Streak
            </span>
            <span className={`text-xl font-black ${streak >= 2 ? 'text-amber-600' : 'text-dark'}`} data-id="element-178">
              {streak}
            </span>
          </div>
          <div className="bg-white rounded-full px-5 py-2 shadow-sm border border-gray-200 flex items-center gap-2" data-id="element-179">
            <span className="text-sm font-bold text-gray-500 uppercase tracking-wider" data-id="element-180">
              Round
            </span>
            <span className="text-xl font-black text-dark" data-id="element-181">
              {currentRoundNum}/{totalRounds}
            </span>
          </div>
        </div>

        {/* Header */}
        <div className="text-center mb-8 md:mb-12" data-id="element-182">
          <h2 className="text-3xl md:text-5xl font-black text-dark mb-3" data-id="element-183">
            {roundData.title}
          </h2>
          <p className="text-lg md:text-xl text-gray-600" data-id="element-184">
            {roundData.question}
          </p>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-12" data-id="element-185">
          {SPECIALTIES.map(spec => <SpecialtyCard key={spec} specialty={spec} isSelected={selectedSpecialty === spec} isCompleted={completedSpecialties.includes(spec)} isDimmed={selectedSpecialty !== null && selectedSpecialty !== spec} onClick={() => handleSpecialtyClick(spec)} data-id="element-186" />)}
        </div>

        {/* Interaction Area */}
        <div className="flex-grow flex flex-col items-center justify-start relative" data-id="element-187">
          <AnimatePresence mode="wait" data-id="element-188">
            {!selectedSpecialty && completedSpecialties.length < SPECIALTIES.length && <motion.div key="prompt" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -20
          }} className="text-xl text-gray-500 font-medium text-center mt-8" data-id="element-189">
                  Select a specialty card to begin
                </motion.div>}

            {selectedSpecialty && !showInsight && <motion.div key="options" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -20
          }} className="w-full max-w-2xl" data-id="element-190">
                <h3 className="text-center text-xl font-bold mb-6 text-dark" data-id="element-191">
                  Match the correct trait for{' '}
                  <span style={{
                color: SPECIALTY_COLORS[selectedSpecialty]
              }} data-id="element-192">
                    {selectedSpecialty}s
                  </span>
                  :
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-193">
                  {roundData.options.map(option => {
                const isSelected = selectedOption === option;
                const isCorrectAnswer = option === roundData.answers[selectedSpecialty];
                const showCorrect = selectedOption && isCorrectAnswer;
                const showWrong = isSelected && !isCorrectAnswer;
                return <motion.button key={option} disabled={selectedOption !== null} onClick={() => handleOptionSelect(option)} animate={showCorrect ? {
                  scale: [1, 1.05, 1],
                  transition: {
                    duration: 0.4
                  }
                } : showWrong ? {
                  x: [0, -6, 6, -4, 4, 0],
                  transition: {
                    duration: 0.4
                  }
                } : {}} className={`
                          p-5 rounded-2xl border-2 text-left text-lg font-medium transition-all duration-300 flex justify-between items-center
                          ${!selectedOption ? 'bg-white border-gray-200 hover:border-gray-400 hover:shadow-md' : ''}
                          ${showCorrect ? 'bg-green-50 border-green-500 text-green-800 shadow-lg shadow-green-100' : ''}
                          ${showWrong ? 'bg-red-50 border-red-500 text-red-800 shadow-lg shadow-red-100' : ''}
                          ${selectedOption && !showCorrect && !showWrong ? 'bg-white border-gray-200 opacity-40' : ''}
                        `} data-id="element-194">
                        <span data-id="element-195">{option}</span>
                        {showCorrect && <CheckCircle2 className="text-green-500" size={24} data-id="element-196" />}
                        {showWrong && <XCircle className="text-red-500" size={24} data-id="element-197" />}
                      </motion.button>;
              })}
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>

        {/* Insight Panel */}
        <AnimatePresence data-id="element-198">
          {showInsight && selectedSpecialty && <motion.div initial={{
          opacity: 0,
          y: 100
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: 100
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="fixed bottom-0 left-0 right-0 p-4 md:p-8 z-50 flex justify-center pointer-events-none" data-id="element-199">
              <div className="bg-white rounded-3xl p-6 md:p-8 shadow-2xl border-t-4 w-full max-w-3xl pointer-events-auto flex flex-col md:flex-row items-center gap-6" style={{
            borderColor: lastResult === 'correct' ? '#22c55e' : '#ef4444'
          }} data-id="element-200">
                <div className={`p-4 rounded-full flex-shrink-0 ${lastResult === 'correct' ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`} data-id="element-201">
                  {lastResult === 'correct' ? <Trophy size={32} data-id="element-202" /> : <Frown size={32} data-id="element-203" />}
                </div>
                <div className="flex-grow text-center md:text-left" data-id="element-204">
                  <h4 className={`text-sm font-bold uppercase tracking-wider mb-1 ${lastResult === 'correct' ? 'text-green-600' : 'text-red-600'}`} data-id="element-205">
                    {lastResult === 'correct' ? '🎉 Correct!' : '😬 Not quite!'}
                    {lastResult === 'correct' && streak >= 3 && ` 🔥 ${streak} streak!`}
                  </h4>
                  <h4 className="text-sm font-bold uppercase tracking-wider mb-2" style={{
                color: SPECIALTY_COLORS[selectedSpecialty]
              }} data-id="element-206">
                    {selectedSpecialty} Insight
                  </h4>
                  <p className="text-base md:text-lg text-dark font-medium leading-relaxed" data-id="element-207">
                    {roundData.insights[selectedSpecialty]}
                  </p>
                </div>
                <button onClick={handleContinue} className="flex-shrink-0 px-8 py-4 bg-dark text-white rounded-full font-bold hover:bg-black transition-colors flex items-center whitespace-nowrap" data-id="element-208">
                  Continue <ArrowRight className="ml-2" size={20} data-id="element-209" />
                </button>
              </div>
            </motion.div>}
        </AnimatePresence>

        {/* Overlay */}
        <AnimatePresence data-id="element-210">
          {showInsight && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 bg-black/5 z-40" data-id="element-211" />}
        </AnimatePresence>
      </div>
    </motion.div>;
}